import {
  GridFilterModel,
  GridCallbackDetails,
  GridSortModel,
  GridInitialState,
  GridRowGroupingModel,
} from '@mui/x-data-grid-premium'
import { useEffect, useRef } from 'react'
import { track } from '@omnea-app/fe-analytics/useAnalytics'

export function useDataGridAnalytics({
  loading,
  rows,
  tableName,
  initialState,
  filterModel,
  sortModel,
  rowGroupingModel,
}: {
  loading: boolean
  rows: unknown[]
  tableName?: string | null
  initialState?: GridInitialState
  filterModel?: GridFilterModel
  sortModel?: GridSortModel
  rowGroupingModel?: GridRowGroupingModel
}) {
  const loadStartRef = useRef<number | null>(null)
  const hasTrackedLoad = useRef(false)

  // Track table load time
  useEffect(() => {
    if (loading && loadStartRef.current === null) {
      loadStartRef.current = performance.now()
      hasTrackedLoad.current = false
    }
  }, [loading])

  useEffect(() => {
    if (!loading && rows && rows.length > 0 && loadStartRef.current && !hasTrackedLoad.current) {
      const durationMs = performance.now() - loadStartRef.current
      track('TableFirstRowsLoaded', {
        tableName: tableName || 'unknown',
        durationMs,
        rowCount: rows.length,
        filterModel,
        sortModel,
        initialState,
        rowGroupingModel,
      })
      hasTrackedLoad.current = true
      loadStartRef.current = null
    }
  }, [loading, rows, tableName, filterModel, initialState, sortModel, rowGroupingModel])

  const trackRowClick = () => {
    track('TableRowClicked', {
      tableName: tableName || 'unknown',
      filterModel,
      sortModel,
      initialState,
      rowGroupingModel,
    })
  }

  const trackFilterChange = (updatedFilterModel: GridFilterModel, _details: GridCallbackDetails) => {
    track('TableFiltersChanged', {
      tableName: tableName || 'unknown',
      filterModel: updatedFilterModel,
      sortModel,
      initialState,
      rowGroupingModel,
    })
  }

  const trackSortChange = (updatedSortModel: GridSortModel, _details: GridCallbackDetails) => {
    track('TableSortModelChanged', {
      tableName: tableName || 'unknown',
      filterModel,
      sortModel: updatedSortModel,
      initialState,
      rowGroupingModel,
    })
  }

  return {
    trackRowClick,
    trackFilterChange,
    trackSortChange,
  }
}
